import type RootStore from "stores/RootStore";
import { RouteType } from "types/RouteType";
import DataMapperProfilesStore from "./DataMapperProfilesStore";

const profilesStore = (rootStore: RootStore) =>
  rootStore.dataMapperProfilesViewStore as DataMapperProfilesStore;

/**
 * Data Mapper → Profiles (plan/2026-10-09_data-mapper-into-hq.md). The first
 * step of the standalone tool's wizard, inside HQ: the client list is HQ's own
 * `clients`, and the chosen client + opened profile live on DataMapperViewStore.
 *
 * Operator tier and above, gated the way HQ gates its other operator areas:
 * the route needs only authentication, the store checks `canUseDataMapper`
 * (auth/tier) before calling anything and the body says why to a viewer, and
 * the API refuses viewers and API keys on every data-mapper service regardless.
 */
const dataMapperProfilesRoute: RouteType[] = [
  {
    name: "Data Mapper",
    path: "/data-mapper/profiles",
    layout: "/app",
    viewportCard: {
      header: {
        enableBackButton: true,
        title: "Data Mapper",
        enableRefreshButton: true,
        component: () => import("./DataMapperProfiles.Header"),
      },
      body: {
        component: () => import("./DataMapperProfiles.Body"),
        grayBackground: true,
      },
    },
    group: "Data Mapper",
    searchAliases: ["data mapper", "mapping profiles", "import mapping", "titan", "client data import"],
    requiresAuth: true,
    requiresPermission: false,
    onEnter: ({ rootStore }) => {
      void profilesStore(rootStore)?.enter();
    },
    onExit: ({ rootStore }) => {
      profilesStore(rootStore)?.reset();
    },
    dialogs: [
      {
        name: "DataMapperProfileFormDialog",
        title: "Data Mapper Profile",
        component: () => import("./dialogs/DataMapperProfileForm.Body"),
        cancelButton: true,
        submitButtonText: "Save",
        height: 500,
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          const store = profilesStore(rootStore);
          const outcome = await store.saveProfile();
          // A failure keeps the dialog open with the reason (the library shows a
          // thrown error in the footer). A 409 closes it: its form is stale, and
          // the page now says what happened above the reloaded list.
          if (outcome === "failed") throw new Error(store.formError ?? "The profile could not be saved.");
        },
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) =>
          profilesStore(rootStore).isFormValid,
      },
    ],
  },
];

export default dataMapperProfilesRoute;
